import { useMemo, useState } from 'react';
import { SectionHead, StatusBar } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { RevenueChart } from '../components/RevenueChart';
import { StatTile } from '../components/StatTile';
import { formatCents } from '../lib/format';
import { useRevenueDailySeries, useTools } from '../lib/queries';

function daysAgoIso(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

export function RevenuePage() {
  const tools = useTools();
  const [toolId, setToolId] = useState<string | null>(null); // null = all tools
  const [days, setDays] = useState(30);

  const range = useMemo(
    () => ({ since: daysAgoIso(days), until: new Date().toISOString() }),
    [days],
  );
  const series = useRevenueDailySeries(toolId, range);

  const total = series.data?.reduce((a, p) => a + p.total_cents, 0) ?? 0;
  const bySource = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const p of series.data ?? []) {
      for (const [k, v] of Object.entries(p.by_source)) acc[k] = (acc[k] ?? 0) + v;
    }
    return acc;
  }, [series.data]);

  const topSources = Object.entries(bySource)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const selectedSlug = tools.data?.find((t) => t.id === toolId)?.slug ?? 'all tools';

  return (
    <div>
      <PageHeader
        breadcrumb="Tool Factory · Revenue"
        title="Revenue"
        subtitle="Daily revenue by source across your portfolio. Adjust the range and filter to a single tool."
        actions={
          <>
            <select
              className="rounded-md border border-border bg-card px-3 py-[8px] font-heading text-[12px] font-medium text-foreground transition-colors hover:bg-secondary/60"
              value={toolId ?? ''}
              onChange={(e) => setToolId(e.target.value || null)}
            >
              <option value="">All tools</option>
              {tools.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.slug}
                </option>
              ))}
            </select>
            <select
              className="rounded-md border border-border bg-card px-3 py-[8px] font-heading text-[12px] font-medium text-foreground transition-colors hover:bg-secondary/60"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
          </>
        }
      />

      <div className="mb-8 grid grid-cols-1 gap-[14px] md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={`Total · ${days}d`}
          accent="gold"
          value={formatCents(total)}
          delta={`${selectedSlug}`}
        />
        {topSources.length === 0 ? (
          <>
            <StatTile label="No revenue yet" value={formatCents(0)} delta="—" />
            <StatTile label="No revenue yet" value={formatCents(0)} delta="—" />
            <StatTile label="No revenue yet" value={formatCents(0)} delta="—" />
          </>
        ) : (
          topSources.map(([k, v]) => (
            <StatTile key={k} label={k} value={formatCents(v)} delta={`${days}-day total`} />
          ))
        )}
      </div>

      <SectionHead
        title="Daily revenue"
        meta={series.isLoading ? 'Loading…' : `${series.data?.length ?? 0} days`}
      />

      <div className="rounded-lg border border-border bg-card p-5">
        {series.isLoading ? (
          <p className="py-12 text-center text-[13px] text-foreground/55">Loading…</p>
        ) : (
          <RevenueChart points={series.data ?? []} />
        )}
      </div>

      <StatusBar left={`${selectedSlug} · ${days}-day range`} right="tools.holdengr.com" />
    </div>
  );
}
